import { secureImageUrl } from "services/utils/payslipTemplate";
import React from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
  Font,
} from "@react-pdf/renderer";
import moment from "moment";
import Roboto from "../../../../../assets/fonts/Roboto/Roboto-Regular.ttf";
import RobotoBold from "../../../../../assets/fonts/Roboto/Roboto-Bold.ttf";
import {
  formatQuantity,
  formatRate,
  getComponentQuantity,
  getComponentRate,
} from "pages/PayRoll/PayslipTemplates/otherEarningsFormat";

Font.register({
  family: "Roboto",
  fonts: [{ src: Roboto }, { src: RobotoBold, fontWeight: 600 }],
  format: "truetype",
});

const styles = StyleSheet.create({
  section: {
    display: "flex",
    width: "49.5%",
    border: "1px solid #ddd",
    padding: 5,
    borderRadius: 5,
    boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
  },
  item: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    margin: 5,
    marginLeft: 0,
    fontSize: 12,
  },
  page: {
    padding: 30,
    color: "#333",
    pageBreakAfter: "always",
    boxSizing: "border-box",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    borderBottom: "1px solid #ddd",
    paddingBottom: 5,
  },
  logoContainer: {
    width: "20%",
    marginRight: 10,
  },
  logo: {
    width: 100,
    height: 40,
    objectFit: "contain",
  },
  logoPlaceholder: {
    width: 80,
    height: 80,
    marginRight: 20,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f5f5f5",
    borderRadius: 4,
  },
  companyInfo: {
    flex: 1,
    textAlign: "center",
  },
  companyName: {
    fontSize: 14,
    fontWeight: 600,
    textTransform: "capitalize",
    marginBottom: 5,
    letterSpacing: 1,
  },
  companyAddress: {
    fontSize: 10,
    color: "#555",
    lineHeight: 1.5,
  },

  // Other Earnings breakdown: Component | Qty | Rate | Total
  otherEarningsHead: {
    display: "flex",
    flexDirection: "row",
    marginBottom: 3,
    borderBottom: "1px dotted #ddd",
  },
  otherEarningsRow: {
    display: "flex",
    flexDirection: "row",
    marginTop: 5,
    marginBottom: 5,
  },
  otherEarningsHeadText: {
    fontSize: 8,
    fontWeight: 600,
  },
  otherEarningsText: {
    fontSize: 7.5,
  },
  otherEarningsTotalText: {
    fontSize: 7.5,
    fontWeight: 600,
  },
  oeName: {
    width: "42%",
  },
  oeQty: {
    width: "17%",
    textAlign: "right",
  },
  oeRate: {
    width: "19%",
    textAlign: "right",
  },
  oeTotal: {
    width: "22%",
    textAlign: "right",
  },
});

const getComponentName = (item) => item?.component_name ?? item?.name ?? "N/A";
const getComponentAmount = (item) =>
  item?.amount ?? item?.value ?? item?.total ?? "N/A";

const PayslipPage = ({ payslipData }) => {
  if (!payslipData) {
    return null;
  }

  const renderSection = (title, data, total) =>
    Array.isArray(data) && data.length ? (
      <View style={styles.section}>
        <View
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            margin: 5,
            marginLeft: 0,
            marginTop: 0,
            marginBottom: 0,
            borderBottom: "1px dotted #ddd",
          }}
        >
          <Text style={{ fontSize: 9, fontWeight: 600, marginBottom: 3 }}>
            {title}
          </Text>
          <Text style={{ fontSize: 9, fontWeight: 600, marginBottom: 3 }}>
            Amount
          </Text>
        </View>
        {data?.map((item, index) => (
          <View style={styles.item} key={`${getComponentName(item)}-${index}`}>
            <Text style={{ fontSize: 8 }}>{getComponentName(item)}</Text>
            <Text style={{ fontSize: 8 }}>Rs. {getComponentAmount(item)}</Text>
          </View>
        ))}
        <View style={styles.item}>
          <Text style={{ fontSize: 8, fontWeight: 600 }}>Total {title}</Text>
          <Text style={{ fontSize: 8, fontWeight: 600 }}>Rs. {total}</Text>
        </View>
      </View>
    ) : null;

  // Other Earnings carry the quantity and rate they were earned on (e.g.
  // "1.5 OT Overtime  99:00 x 68.55"), so they get their own Qty/Rate columns
  // instead of the plain name/amount pair the other sections use.
  const renderOtherEarningsSection = (title, data, total) =>
    Array.isArray(data) && data.length ? (
      <View style={styles.section}>
        <View style={styles.otherEarningsHead}>
          <Text style={[styles.otherEarningsHeadText, styles.oeName]}>
            {title}
          </Text>
          <Text style={[styles.otherEarningsHeadText, styles.oeQty]}>Qty</Text>
          <Text style={[styles.otherEarningsHeadText, styles.oeRate]}>
            Rate
          </Text>
          <Text style={[styles.otherEarningsHeadText, styles.oeTotal]}>
            Total
          </Text>
        </View>
        {data?.map((item, index) => (
          <View
            style={styles.otherEarningsRow}
            key={`${getComponentName(item)}-${index}`}
          >
            <Text style={[styles.otherEarningsText, styles.oeName]}>
              {getComponentName(item)}
            </Text>
            <Text style={[styles.otherEarningsText, styles.oeQty]}>
              {formatQuantity(getComponentQuantity(item))}
            </Text>
            <Text style={[styles.otherEarningsText, styles.oeRate]}>
              {formatRate(getComponentRate(item))}
            </Text>
            <Text style={[styles.otherEarningsText, styles.oeTotal]}>
              {getComponentAmount(item)}
            </Text>
          </View>
        ))}
        <View style={styles.otherEarningsRow}>
          <Text style={[styles.otherEarningsTotalText, styles.oeName]}>
            Total {title}
          </Text>
          <Text style={[styles.otherEarningsTotalText, styles.oeQty]} />
          <Text style={[styles.otherEarningsTotalText, styles.oeRate]} />
          <Text style={[styles.otherEarningsTotalText, styles.oeTotal]}>
            Rs. {total}
          </Text>
        </View>
      </View>
    ) : null;

  return (
    <Page size="A4" style={styles.page}>
      <View style={styles.header}>
        {payslipData?.company_logo_url ? (
          <View style={styles.logoContainer}>
            <Image style={styles.logo} src={secureImageUrl(payslipData?.company_logo_url)} />
          </View>
        ) : (
          <View style={styles.logoPlaceholder}></View>
        )}

        <View style={styles.companyInfo}>
          <Text style={styles.companyName}>
            {payslipData?.company_name || "Organflow"}
          </Text>

          {payslipData?.company_address && (
            <Text style={styles.companyAddress}>
              {payslipData?.company_address}
            </Text>
          )}
        </View>
      </View>

      <View
        style={{
          marginTop: 10,
          marginBottom: 3,
          alignItems: "center",
        }}
      >
        <Text
          style={{
            fontSize: 10,
            fontWeight: 600,
          }}
        >
          Employee Pay Summary of{" "}
          {moment(payslipData?.pay_date, "DD-MM-YYYY").format("MMMM, YYYY")}
        </Text>
      </View>

      <View style={{ marginTop: 10, marginBottom: 5 }}>
        {[
          {
            left: `Employee Name: ${payslipData?.user_name || "N/A"}`,
            right: `Monthly Salary: ${
              payslipData?.monthly_salary ? payslipData?.monthly_salary : "N/A"
            }`,
          },
          {
            left: `Designation: ${payslipData?.user_designation || "N/A"}`,
            right: `Total Shift Payable: ${
              payslipData?.total_payable_shift || 0
            }`,
          },
          {
            left: `Pay Date: ${payslipData?.pay_date || "N/A"}`,
            right: `Net Payable Shift: ${payslipData?.net_payable_shift || 0}`,
          },
          {
            left: `Pay Period: ${moment(
              payslipData?.pay_date,
              "DD-MM-YYYY"
            ).format("MMMM-YYYY")}`,
            right: `Net Salary: ${
              payslipData?.net_pay ? payslipData?.net_pay : "N/A"
            }`,
          },
        ].map((row, index) => (
          <View
            key={index}
            style={{
              flexDirection: "row",
              alignItems: "center",
              width: "100%",
            }}
          >
            <View style={{ flex: 1, padding: 4, paddingVertical: 1 }}>
              <Text style={{ fontSize: 9, padding: 2, textAlign: "left" }}>
                {row.left}
              </Text>
            </View>
            <View style={{ flex: 1, padding: 4, paddingVertical: 1 }}>
              <Text style={{ fontSize: 9, padding: 2, textAlign: "left" }}>
                {row.right}
              </Text>
            </View>
          </View>
        ))}
      </View>

      <View
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          marginTop: 5,
        }}
      >
        {renderSection(
          "Earnings",
          payslipData?.earnings,
          payslipData?.total_earnings
        )}
        {renderSection(
          "Deductions",
          payslipData?.deduction,
          payslipData?.total_deductions
        )}
      </View>

      <View
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          marginTop: 5,
        }}
      >
        {renderOtherEarningsSection(
          "Other Earnings",
          payslipData?.other_earnings,
          payslipData?.total_other_earnings
        )}
        {renderSection(
          "Other Deductions",
          payslipData?.other_deduction,
          payslipData?.total_other_deductions
        )}
      </View>

      <View
        style={{
          marginTop: 5,
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          padding: 5,
          backgroundColor: "#84deb8",
          borderRadius: 5,
        }}
      >
        <Text style={{ fontSize: 9, fontWeight: 600 }}>Total Net Payable</Text>
        <Text style={{ fontSize: 9, fontWeight: 600 }}>
          Rs. {payslipData?.net_pay}
        </Text>
      </View>

      <View
        style={{
          marginBottom: 5,
          padding: 5,
        }}
      >
        <Text
          style={{
            fontSize: 8,
          }}
        >
          Total Net Payable: Rs. {payslipData?.net_pay} (Rupees{" "}
          {payslipData?.net_pay_in_words})
        </Text>
      </View>

      <Text style={{ textAlign: "center", marginTop: 3, fontSize: 8 }}>
        --- This is a system generated payslip ---
      </Text>
    </Page>
  );
};

const BulkPayslipData = ({ employeesPdfData = [] }) => {
  return (
    <Document>
      {employeesPdfData
        .filter((emp) => emp?.payslipData)
        .map((employee, index) => (
          <PayslipPage key={index} payslipData={employee.payslipData} />
        ))}
    </Document>
  );
};

export default BulkPayslipData;
